import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { PdfPageItem } from '../types'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  GripIcon,
  TrashIcon,
} from './Icons'

type SortablePageProps = {
  page: PdfPageItem
  position: number
  total: number
  onMove: (id: string, direction: -1 | 1) => void
  onRemove: (id: string) => void
  onOpen: (page: PdfPageItem) => void
}

export default function SortablePage({
  page,
  position,
  total,
  onMove,
  onRemove,
  onOpen,
}: SortablePageProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id })

  return (
    <article
      ref={setNodeRef}
      className={`page-card${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onDoubleClick={(event) => {
        if ((event.target as HTMLElement).closest('button')) return
        onOpen(page)
      }}
      title="드래그해 순서 변경 · 더블클릭해 크게 보기"
    >
      <div className="page-card__preview">
        <img
          src={page.previewUrl}
          alt={`${page.fileName}의 ${page.sourcePageIndex + 1}페이지 미리보기`}
          draggable={false}
        />
        <span className="page-card__number" aria-label={`병합 순서 ${position + 1}`}>
          {String(position + 1).padStart(2, '0')}
        </span>
        <button
          className="icon-button page-card__delete"
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={() => onRemove(page.id)}
          aria-label={`${position + 1}번째 페이지 삭제`}
          title="페이지 삭제"
        >
          <TrashIcon />
        </button>
        <span className="page-card__open-hint" aria-hidden="true">더블클릭해 크게 보기</span>
      </div>

      <div className="page-card__info">
        <div className="page-card__text">
          <strong title={page.fileName}>{page.fileName}</strong>
          <span>원본 {page.sourcePageIndex + 1} 페이지</span>
        </div>
        <span className="drag-indicator" aria-hidden="true">
          <GripIcon />
        </span>
      </div>

      <div className="page-card__mobile-controls" aria-label="페이지 순서 조정">
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={() => onMove(page.id, -1)}
          disabled={position === 0}
          aria-label="한 칸 앞으로"
        >
          <ArrowLeftIcon />
          앞으로
        </button>
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={() => onMove(page.id, 1)}
          disabled={position === total - 1}
          aria-label="한 칸 뒤로"
        >
          뒤로
          <ArrowRightIcon />
        </button>
      </div>
    </article>
  )
}
